import { Button } from '../../../../../components/Button';
import { formatMoney } from '../../../../../utils/formatMoney';
import { ICashMePendingItem, ICreateCashMeProposal } from '../../types';
import {
  COLLATERAL_TYPE_OPTIONS,
  CREDIT_PURPOSE_OPTIONS,
  MARITAL_STATUS_OPTIONS,
} from '../../constants';
import { WizardShell } from '../WizardShell/WizardShell';
import styles from '../WizardShell/WizardShell.module.css';

interface IReviewStep {
  payload: ICreateCashMeProposal;
  isSubmitting: boolean;
  pendingItems?: ICashMePendingItem[];
  errorMessage?: string;
  onBack: () => void;
  onSubmit: () => void;
}

const labelOf = (options: { value: string; label: string }[], value?: string) =>
  options.find((option) => option.value === value)?.label ?? value ?? '—';

const formatAddress = (address?: {
  street?: string;
  number?: string;
  district?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}) => {
  if (!address) return '—';
  return [
    [address.street, address.number].filter(Boolean).join(', '),
    address.district,
    [address.city, address.state].filter(Boolean).join(' - '),
    address.zipCode,
  ]
    .filter(Boolean)
    .join(' · ');
};

export function ReviewStep({
  payload,
  isSubmitting,
  pendingItems = [],
  errorMessage,
  onBack,
  onSubmit,
}: IReviewStep) {
  const { proponent, collaterals, creditPurposes } = payload;
  const collateral = collaterals[0];
  const isLegal = proponent.personType === 'legal';

  const rows: [string, string][] = [
    ['Nome', proponent.name],
    [isLegal ? 'CNPJ' : 'CPF', proponent.document],
    ['E-mail', proponent.email],
    ['Telefone', proponent.phone],
    ['Endereço', formatAddress(proponent.address)],
    [
      isLegal ? 'Faturamento mensal' : 'Renda mensal',
      formatMoney(proponent.monthlyIncome) ?? '—',
    ],
    ['Finalidade', labelOf(CREDIT_PURPOSE_OPTIONS, creditPurposes[0])],
    ['Tipo do imóvel', labelOf(COLLATERAL_TYPE_OPTIONS, collateral?.type)],
    ['Valor do imóvel', formatMoney(collateral?.value) ?? '—'],
    ['Endereço do imóvel', formatAddress(collateral?.address)],
    ['Imóvel quitado', collateral?.financed ? 'Não' : 'Sim'],
  ];

  if (!isLegal) {
    rows.splice(5, 0, [
      'Estado civil',
      labelOf(MARITAL_STATUS_OPTIONS, proponent.maritalStatus),
    ]);

    if (proponent.spouse?.name) {
      rows.splice(6, 0, [
        proponent.maritalStatus === 'MARRIED' ? 'Cônjuge' : 'Companheiro(a)',
        proponent.spouse.name,
      ]);
    }
  } else {
    proponent.shareholdings?.forEach((item, index) => {
      rows.splice(5 + index, 0, [
        `Sócio ${index + 1}`,
        `${item.document} · ${item.percentage}%`,
      ]);
    });
  }

  const sortedPendingItems = [...pendingItems].sort((a, b) =>
    a.level === b.level ? 0 : a.level === 'ERROR' ? -1 : 1
  );

  return (
    <WizardShell
      step={4}
      totalSteps={4}
      title="Revisão"
      helper="Confira os dados antes de enviar. Depois do envio, a equipe CashMe entra em contato para dar sequência."
    >
      <div>
        {rows.map(([label, value]) => (
          <div
            key={label}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 16,
              padding: '10px 0',
              borderBottom: '1px solid var(--background-input)',
              fontSize: 14,
              color: 'var(--color-text)',
            }}
          >
            <span style={{ opacity: 0.6 }}>{label}</span>
            <span style={{ fontWeight: 600, textAlign: 'right' }}>{value}</span>
          </div>
        ))}
      </div>

      {(sortedPendingItems.length > 0 || errorMessage) && (
        <div className={styles.pendingBox} role="alert">
          <p className={styles.pendingTitle}>
            {errorMessage ?? 'A CashMe apontou pendências na proposta:'}
          </p>
          <ul className={styles.pendingList}>
            {sortedPendingItems.map((item, index) => (
              <li
                key={`${item.code}-${index}`}
                className={
                  item.level === 'ERROR'
                    ? styles.pendingError
                    : styles.pendingWarning
                }
              >
                {item.message || item.code}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className={styles.footer}>
        <Button
          type="button"
          color="flat"
          text="Voltar"
          onClick={onBack}
          disabled={isSubmitting}
        />
        <Button
          type="button"
          color="primary"
          text="Enviar proposta"
          onClick={onSubmit}
          isLoading={isSubmitting}
          disabled={isSubmitting}
        />
      </div>
    </WizardShell>
  );
}
